import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { Order, Return } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { returnedValue } from '../schemas/return-rules';

/** The returned units with the condition recorded at inspection and whether they were restocked. */
export function ReturnLinesCard({ ret, order }: { ret: Return; order: Order | undefined }) {
  const { t } = useTranslation('returns');
  const format = useFormat();
  const lines = new Map(order?.lines.map((line) => [line.id, line]) ?? []);

  return (
    <FormSection
      tour="returns.detail.lines"
      title={t('lines.title')}
      description={
        order ? t('lines.value', { amount: format.money(returnedValue(ret, order)) }) : undefined
      }
    >
      <Table aria-label={t('lines.title')}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('lines.item')}</TableHead>
            <TableHead className="text-right">{t('lines.quantity')}</TableHead>
            <TableHead>{t('lines.condition')}</TableHead>
            <TableHead>{t('lines.restock')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ret.lines.map((item) => {
            const line = lines.get(item.orderLineId);
            return (
              <TableRow key={item.orderLineId}>
                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">{line?.title ?? t('lines.unknown')}</span>
                    {line?.variantTitle ? (
                      <span className="text-muted-foreground text-xs">{line.variantTitle}</span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums">{item.quantity}</TableCell>
                <TableCell>
                  {item.condition ? (
                    <Badge
                      variant="outline"
                      className={
                        item.condition === 'damaged'
                          ? 'bg-destructive/10 text-destructive border-transparent'
                          : undefined
                      }
                    >
                      {t(`condition.${item.condition}`)}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">{t('lines.notInspected')}</span>
                  )}
                </TableCell>
                <TableCell>
                  {item.restock ? (
                    t('lines.restocked')
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </FormSection>
  );
}
